import { AlertTriangle, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { TrashStats } from '@/types/api/trash'

interface TrashStatsComponentProps {
  stats: TrashStats | undefined
  isLoading: boolean
  showCleanupButton?: boolean
  onCleanup?: () => void
  isCleaningUp?: boolean
}

export function TrashStatsComponent({
  stats,
  isLoading,
  showCleanupButton = false,
  onCleanup,
  isCleaningUp = false,
}: TrashStatsComponentProps) {
  if (isLoading || !stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Trash Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-neutral-500">Loading...</div>
        </CardContent>
      </Card>
    )
  }

  const hasExpired = stats.expiredItems > 0

  return (
    <Card className={hasExpired ? 'border-amber-200 bg-amber-50' : ''}>
      <CardHeader>
        <CardTitle>Trash Statistics</CardTitle>
        <CardDescription>Items in trash (auto-deleted after {stats.retentionDays} days)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <span className="text-sm font-medium text-neutral-500">Total Items</span>
            <p className="text-2xl font-semibold">{stats.totalTrashItems}</p>
          </div>
          <div className="space-y-1">
            <span className="text-sm font-medium text-neutral-500">Expired Items</span>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-semibold text-amber-600">{stats.expiredItems}</p>
              {hasExpired && <AlertTriangle className="h-4 w-4 text-amber-500" />}
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-sm font-medium text-neutral-500">Retention Period</span>
            <p className="text-2xl font-semibold">{stats.retentionDays}d</p>
          </div>
        </div>

        {hasExpired && (
          <div className="rounded-md bg-amber-100 p-3">
            <p className="text-sm text-amber-900">
              {stats.expiredItems} item{stats.expiredItems !== 1 ? 's' : ''} are ready for permanent deletion.
              {showCleanupButton && ' Click cleanup to remove them.'}
            </p>
          </div>
        )}

        {showCleanupButton && (
          <Button
            onClick={onCleanup}
            disabled={isCleaningUp || stats.expiredItems === 0}
            className="w-full"
            variant={hasExpired ? 'default' : 'outline'}
          >
            {isCleaningUp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cleanup Expired Items
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
